import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fetchAnalytical } from "./api";
import type { AnalyticalData, AnalyticalRange } from "./types";
import { fmtCLP, fmtInt } from "./format";
import { RangeTabs } from "./shared/RangeTabs";
import { LoadingBlock, ErrorBlock, EmptyBlock } from "./shared/StateBlocks";

const RANGE_OPTIONS: { value: AnalyticalRange; label: string }[] = [
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "all", label: "Todo" },
];

const DOW_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export function AnalyticalScreen() {
  const [range, setRange] = useState<AnalyticalRange>("30d");

  const query = useQuery({
    queryKey: ["dashboard", "analytical", range],
    queryFn: () => fetchAnalytical(range),
    refetchInterval: 10 * 60_000,
  });

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Dashboard · Analítico</p>
          <h1 className="nx-h1">Comportamiento de usuarios</h1>
          <p className="text-sm text-fg-muted mt-1">
            Embudo de conversión, patrones temporales, distribución por club y cohortes mensuales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeTabs options={RANGE_OPTIONS} value={range} onChange={setRange} />
          <button
            onClick={() => query.refetch()}
            className="nx-btn-secondary"
            disabled={query.isFetching}
            aria-label="Actualizar"
          >
            <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando dashboard analítico…" />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <DashboardBody data={query.data} />
      )}
    </div>
  );
}

function DashboardBody({ data }: { data: AnalyticalData }) {
  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="nx-card p-4">
          <h2 className="nx-h2 mb-1">Embudo de conversión</h2>
          <p className="text-2xs text-fg-subtle mb-4">
            Del registro a la asistencia al evento
          </p>
          <Funnel funnel={data.funnel} />
        </div>

        <div className="nx-card p-4">
          <h2 className="nx-h2 mb-1">Ventas por club</h2>
          <p className="text-2xs text-fg-subtle mb-4">Top 8 por ingresos</p>
          {data.byClub.length === 0 ? (
            <EmptyBlock label="Sin ventas en el período." />
          ) : (
            <ClubBars data={data.byClub} />
          )}
        </div>
      </section>

      <section className="nx-card p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="nx-h2">Patrón de ventas · día × hora</h2>
          <span className="text-2xs text-fg-subtle">
            tickets vendidos · hora de Chile
          </span>
        </div>
        <Heatmap heatmap={data.heatmap} />
      </section>

      <section className="nx-card p-4">
        <h2 className="nx-h2 mb-3">Top eventos del período</h2>
        {data.topEvents.length === 0 ? (
          <EmptyBlock label="Sin eventos con ventas en el período." />
        ) : (
          <TopEventsTable data={data.topEvents} />
        )}
      </section>

      <section className="nx-card p-4">
        <h2 className="nx-h2 mb-1">Cohortes de usuarios</h2>
        <p className="text-2xs text-fg-subtle mb-4">
          % de usuarios que compraron tickets en su mes de registro y siguientes
        </p>
        {data.cohorts.length === 0 ? (
          <EmptyBlock label="Aún no hay cohortes que mostrar." />
        ) : (
          <CohortTable data={data.cohorts} />
        )}
      </section>
    </>
  );
}

function Funnel({ funnel }: { funnel: AnalyticalData["funnel"] }) {
  const max = Math.max(...funnel.map((f) => f.count), 1);
  const first = funnel[0]?.count || 0;
  return (
    <div className="flex flex-col gap-2">
      {funnel.map((stage, i) => {
        const widthPct = Math.max(8, Math.round((stage.count / max) * 100));
        const convPct = first > 0 ? Math.round((stage.count / first) * 100) : 0;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-44 text-sm text-fg-muted truncate">{stage.stage}</div>
            <div className="flex-1 bg-bg-subtle rounded h-8 relative overflow-hidden">
              <div
                className="h-full bg-accent flex items-center px-2 text-xs font-semibold text-fg-inverted transition-all duration-300"
                style={{ width: `${widthPct}%` }}
              >
                {fmtInt(stage.count)}
              </div>
            </div>
            <div className="w-12 text-right text-xs text-fg-subtle tabular-nums">
              {convPct}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ClubBars({ data }: { data: AnalyticalData["byClub"] }) {
  const chartData = data.map((c) => ({ name: c.name, revenue: c.revenue, qty: c.qty }));
  return (
    <div className="w-full h-72">
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
        >
          <CartesianGrid stroke="var(--nx-border-subtle)" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => (v >= 1_000_000 ? (v / 1_000_000).toFixed(1) + "M" : (v / 1000).toFixed(0) + "k")}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: "var(--nx-fg)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={110}
          />
          <Tooltip
            content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null;
              const item = payload[0].payload;
              return (
                <div className="nx-card p-2 text-xs shadow-lg min-w-[160px]">
                  <div className="font-semibold text-fg mb-1">{item.name}</div>
                  <div className="flex justify-between gap-3">
                    <span className="text-fg-muted">Ingresos</span>
                    <span className="text-fg tabular-nums">{fmtCLP(item.revenue)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-fg-muted">Tickets</span>
                    <span className="text-fg tabular-nums">{fmtInt(item.qty)}</span>
                  </div>
                </div>
              );
            }}
            cursor={{ fill: "var(--nx-bg-hover)" }}
          />
          <Bar dataKey="revenue" fill="var(--nx-accent)" radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Heatmap({ heatmap }: { heatmap: number[][] }) {
  const maxVal = Math.max(...heatmap.flat(), 1);
  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="grid gap-0.5" style={{ gridTemplateColumns: "60px repeat(24, minmax(20px, 1fr))" }}>
          <div />
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="text-2xs text-fg-subtle text-center tabular-nums">
              {h % 3 === 0 ? `${h}h` : ""}
            </div>
          ))}
          {DOW_LABELS.map((label, dow) => (
            <Row key={dow} label={label} row={heatmap[dow] || []} maxVal={maxVal} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  row,
  maxVal,
}: {
  label: string;
  row: number[];
  maxVal: number;
}) {
  return (
    <>
      <div className="text-2xs text-fg-muted text-right pr-2 self-center">{label}</div>
      {row.map((v, h) => {
        const intensity = v / maxVal;
        const bg =
          v === 0
            ? "var(--nx-bg-subtle)"
            : `color-mix(in srgb, var(--nx-accent) ${Math.max(8, Math.round(intensity * 100))}%, var(--nx-bg-card))`;
        return (
          <div
            key={h}
            title={`${label} ${h}h · ${fmtInt(v)} tickets`}
            className="h-6 rounded-sm border border-border-subtle cursor-help transition-transform hover:scale-110"
            style={{ background: bg }}
          />
        );
      })}
    </>
  );
}

function TopEventsTable({ data }: { data: AnalyticalData["topEvents"] }) {
  return (
    <div className="overflow-x-auto">
      <table className="nx-table">
        <thead>
          <tr>
            <th>Evento</th>
            <th>Club</th>
            <th>Fecha</th>
            <th className="text-right">Tickets</th>
            <th className="text-right">Llenado</th>
            <th className="text-right">Ticket promedio</th>
            <th className="text-right">Ingresos</th>
          </tr>
        </thead>
        <tbody>
          {data.map((ev) => (
            <tr key={ev.id}>
              <td className="font-medium">{ev.nombre}</td>
              <td className="text-fg-muted">{ev.club}</td>
              <td className="text-fg-muted text-2xs">{formatDateShort(ev.fecha)}</td>
              <td className="text-right tabular-nums">{fmtInt(ev.qty)}</td>
              <td className="text-right tabular-nums">
                {ev.fillRate !== null ? `${ev.fillRate}%` : "—"}
              </td>
              <td className="text-right tabular-nums">{fmtCLP(ev.avgTicketPrice)}</td>
              <td className="text-right font-semibold tabular-nums">{fmtCLP(ev.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CohortTable({ data }: { data: AnalyticalData["cohorts"] }) {
  const cellBg = (pct: number) =>
    pct === 0
      ? "var(--nx-bg-subtle)"
      : `color-mix(in srgb, var(--nx-accent) ${Math.max(10, Math.round(pct))}%, var(--nx-bg-card))`;

  return (
    <div className="overflow-x-auto">
      <table className="nx-table">
        <thead>
          <tr>
            <th>Cohorte</th>
            <th className="text-right">Tamaño</th>
            <th className="text-right">M+0</th>
            <th className="text-right">M+1</th>
            <th className="text-right">M+2</th>
            <th className="text-right">M+3</th>
          </tr>
        </thead>
        <tbody>
          {data.map((c) => {
            const pct = (n: number) => (c.size > 0 ? (n / c.size) * 100 : 0);
            return (
              <tr key={c.cohort}>
                <td className="font-medium">{c.cohort}</td>
                <td className="text-right tabular-nums text-fg-muted">{fmtInt(c.size)}</td>
                <CohortCell value={c.m0} pct={pct(c.m0)} bg={cellBg(pct(c.m0))} />
                <CohortCell value={c.m1} pct={pct(c.m1)} bg={cellBg(pct(c.m1))} />
                <CohortCell value={c.m2} pct={pct(c.m2)} bg={cellBg(pct(c.m2))} />
                <CohortCell value={c.m3} pct={pct(c.m3)} bg={cellBg(pct(c.m3))} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CohortCell({ value, pct, bg }: { value: number; pct: number; bg: string }) {
  return (
    <td className="text-right tabular-nums" style={{ background: bg }}>
      <div className="text-fg font-medium">{fmtInt(value)}</div>
      <div className="text-2xs text-fg-subtle">{pct.toFixed(0)}%</div>
    </td>
  );
}

function formatDateShort(fecha: string | null): string {
  if (!fecha) return "—";
  try {
    return new Date(fecha + "T00:00:00").toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return fecha;
  }
}
